import { useCallback, useEffect, useState } from "react";
import { useAdminFetch } from "../lib/admin-api";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/Toast";

interface Signup {
  id: string;
  name: string;
  email: string;
  brand_name: string;
  website: string;
  wants_api_access: boolean;
  notes: string | null;
  status: string;
  created_tenant_id: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  rejection_reason: string | null;
  created_at: string;
}

type Filter = "pending" | "approved" | "rejected" | "all";
const FILTERS: Filter[] = ["pending", "approved", "rejected", "all"];

function statusPill(status: string): string {
  switch (status) {
    case "approved":
      return "bg-green-100 text-green-800 border-green-200";
    case "rejected":
      return "bg-red-100 text-red-800 border-red-200";
    default:
      return "bg-amber-100 text-amber-800 border-amber-200";
  }
}

function formatDate(iso: string | null, fallback = "—"): string {
  if (!iso) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  return new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "numeric" }).format(d);
}

/** Generate a random 12-char password (admin sets the user's initial password). */
function generatePassword(): string {
  const charset = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%";
  const cryptoObj = window.crypto;
  const bytes = new Uint32Array(12);
  cryptoObj.getRandomValues(bytes);
  return Array.from(bytes, (b) => charset[b % charset.length]).join("");
}

/** Mirror of server slugifyBrand so the dialog pre-fill matches the default. */
function slugifyBrand(brandName: string): string {
  return (
    brandName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "brand"
  );
}

export default function Signups() {
  const adminFetch = useAdminFetch();
  const toast = useToast();

  const [filter, setFilter] = useState<Filter>("pending");
  const [signups, setSignups] = useState<Signup[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const [approveTarget, setApproveTarget] = useState<Signup | null>(null);
  const [approveSlug, setApproveSlug] = useState("");
  const [approvePassword, setApprovePassword] = useState("");
  const [approving, setApproving] = useState(false);

  const [rejectTarget, setRejectTarget] = useState<Signup | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const [busyRow, setBusyRow] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminFetch<{ data: Signup[] }>(`/api/admin/signups?status=${filter}`);
      setSignups(r.data || []);
    } catch (err) {
      toast.push(`Failed to load signups: ${(err as Error).message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [adminFetch, toast, filter]);

  useEffect(() => {
    load();
  }, [load]);

  const openApprove = (s: Signup) => {
    setApproveTarget(s);
    setApproveSlug(slugifyBrand(s.brand_name));
    setApprovePassword(generatePassword());
  };

  const confirmApprove = async () => {
    if (!approveTarget) return;
    if (approvePassword.length < 8) {
      toast.push("Initial password must be at least 8 characters.", "error");
      return;
    }
    setApproving(true);
    try {
      await adminFetch<{ tenant_id: string; tenant_slug: string }>(
        `/api/admin/signups/${encodeURIComponent(approveTarget.id)}/approve`,
        {
          method: "POST",
          body: JSON.stringify({ tenant_slug: approveSlug.trim() || undefined, password: approvePassword }),
        }
      );
      toast.push(`Approved — welcome email sent to ${approveTarget.email}`, "success");
      setApproveTarget(null);
      await load();
    } catch (err) {
      toast.push(`Approve failed: ${(err as Error).message}`, "error");
    } finally {
      setApproving(false);
    }
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setRejecting(true);
    try {
      await adminFetch(`/api/admin/signups/${encodeURIComponent(rejectTarget.id)}/reject`, {
        method: "POST",
        body: JSON.stringify({ reason: rejectReason.trim() || undefined }),
      });
      toast.push("Rejected", "success");
      setRejectTarget(null);
      setRejectReason("");
      await load();
    } catch (err) {
      toast.push(`Reject failed: ${(err as Error).message}`, "error");
    } finally {
      setRejecting(false);
    }
  };

  const sendPasswordReset = async (s: Signup) => {
    if (!window.confirm(`Send a password reset email to ${s.email}?`)) return;
    setBusyRow(s.id);
    try {
      await adminFetch(`/api/admin/signups/${encodeURIComponent(s.id)}/resend-link`, { method: "POST" });
      toast.push(`Password reset sent to ${s.email}`, "success");
    } catch (err) {
      toast.push(`Send failed: ${(err as Error).message}`, "error");
    } finally {
      setBusyRow(null);
    }
  };

  const revoke = async (s: Signup) => {
    if (
      !window.confirm(
        `Revoke access for ${s.brand_name}? This invalidates all outstanding magic links and disables their tenant user.`
      )
    )
      return;
    setBusyRow(s.id);
    try {
      await adminFetch(`/api/admin/signups/${encodeURIComponent(s.id)}/revoke`, { method: "POST" });
      toast.push("Access revoked", "success");
      await load();
    } catch (err) {
      toast.push(`Revoke failed: ${(err as Error).message}`, "error");
    } finally {
      setBusyRow(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Brand Signups</h2>
        <p className="text-sm text-gray-600 mt-1">
          Access requests from the public signup form. Approve to provision a tenant with an initial password and email a welcome notice.
        </p>
      </div>

      <div className="flex items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-xs font-medium border capitalize transition-colors ${
              filter === f
                ? "bg-accent-500 text-white border-accent-500"
                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        {loading && signups.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        ) : signups.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-600">No signups.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Date</th>
                <th className="text-left px-4 py-2 font-medium">Name</th>
                <th className="text-left px-4 py-2 font-medium">Email</th>
                <th className="text-left px-4 py-2 font-medium">Brand</th>
                <th className="text-left px-4 py-2 font-medium">Website</th>
                <th className="text-left px-4 py-2 font-medium">API?</th>
                <th className="text-left px-4 py-2 font-medium">Notes</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {signups.map((s) => {
                const isExpanded = expanded[s.id];
                const noteText = s.notes || "";
                const truncated = noteText.length > 60 && !isExpanded;
                return (
                  <tr key={s.id} className="border-t border-gray-100 align-top">
                    <td className="px-4 py-2 text-gray-500 text-xs whitespace-nowrap" title={s.created_at}>
                      {formatDate(s.created_at)}
                    </td>
                    <td className="px-4 py-2 text-gray-900">{s.name}</td>
                    <td className="px-4 py-2 text-gray-700">{s.email}</td>
                    <td className="px-4 py-2 text-gray-900">{s.brand_name}</td>
                    <td className="px-4 py-2">
                      <a
                        href={s.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent-700 hover:underline break-all"
                      >
                        {s.website}
                      </a>
                    </td>
                    <td className="px-4 py-2">
                      {s.wants_api_access ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-blue-100 text-blue-800 border-blue-200">
                          API
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-600 max-w-[16rem]">
                      {noteText ? (
                        <>
                          <span>{truncated ? `${noteText.slice(0, 60)}…` : noteText}</span>
                          {noteText.length > 60 && (
                            <button
                              className="ml-1 text-xs text-accent-700 hover:underline"
                              onClick={() => setExpanded((p) => ({ ...p, [s.id]: !isExpanded }))}
                            >
                              {isExpanded ? "less" : "more"}
                            </button>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusPill(s.status)}`}>
                        {s.status}
                      </span>
                      {s.status === "approved" && s.created_tenant_id && (
                        <a
                          href={`#/tenants/${encodeURIComponent(s.created_tenant_id)}`}
                          className="mt-1 block font-mono text-xs text-accent-700 hover:underline break-all"
                          title="Open tenant"
                        >
                          {s.created_tenant_id}
                        </a>
                      )}
                      {s.status === "rejected" && s.rejection_reason && (
                        <div className="mt-1 text-xs text-gray-500">{s.rejection_reason}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {s.status === "pending" ? (
                        <div className="flex items-center gap-2">
                          <Button variant="primary" size="sm" onClick={() => openApprove(s)}>
                            Approve
                          </Button>
                          <Button variant="secondary" size="sm" onClick={() => setRejectTarget(s)}>
                            Reject
                          </Button>
                        </div>
                      ) : s.status === "approved" && s.created_tenant_id ? (
                        <div className="flex items-center gap-2">
                          <Button variant="primary" size="sm" disabled={busyRow === s.id} onClick={() => sendPasswordReset(s)}>
                            {busyRow === s.id ? "…" : "Send Password Reset"}
                          </Button>
                          <Button variant="secondary" size="sm" disabled={busyRow === s.id} onClick={() => revoke(s)}>
                            Revoke
                          </Button>
                          <a
                            href={`#/tenants/${encodeURIComponent(s.created_tenant_id)}`}
                            className="text-xs text-accent-700 hover:underline"
                          >
                            View Tenant
                          </a>
                        </div>
                      ) : s.status === "rejected" && s.rejection_reason ? (
                        <span className="text-xs text-gray-400" title={s.rejection_reason}>
                          —
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {approveTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={() => !approving && setApproveTarget(null)}>
          <div className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900">Approve signup</h3>
            <p className="text-sm text-gray-600 mt-1">
              A tenant will be created with an initial password, and a welcome email sent to{" "}
              <span className="font-medium">{approveTarget.email}</span>.
            </p>
            <label className="mt-4 block text-xs text-gray-500">
              Tenant slug
              <input
                type="text"
                value={approveSlug}
                onChange={(e) => setApproveSlug(e.target.value)}
                className="mt-1 w-full text-sm border border-gray-200 rounded-md px-2 py-1 font-mono"
              />
            </label>
            <label className="mt-3 block text-xs text-gray-500">
              Initial password
              <div className="mt-1 flex gap-2">
                <input
                  type="text"
                  value={approvePassword}
                  onChange={(e) => setApprovePassword(e.target.value)}
                  className="flex-1 text-sm border border-gray-200 rounded-md px-2 py-1 font-mono"
                />
                <Button variant="secondary" size="sm" type="button" onClick={() => setApprovePassword(generatePassword())}>
                  Generate
                </Button>
              </div>
              <span className="mt-1 block text-[11px] text-gray-400">
                Share this with the user — it is shown only here. Min 8 characters.
              </span>
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setApproveTarget(null)} disabled={approving}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={confirmApprove}
                disabled={approving || !approveSlug.trim() || approvePassword.length < 8}
              >
                {approving ? "Approving…" : "Approve"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={() => !rejecting && setRejectTarget(null)}>
          <div className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900">Reject signup</h3>
            <p className="text-sm text-gray-600 mt-1">
              Reject the request from <span className="font-medium">{rejectTarget.brand_name}</span>. No email is sent.
            </p>
            <label className="mt-4 block text-xs text-gray-500">
              Reason (optional)
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                className="mt-1 w-full text-sm border border-gray-200 rounded-md px-2 py-1"
              />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setRejectTarget(null)} disabled={rejecting}>
                Cancel
              </Button>
              <Button variant="primary" onClick={confirmReject} disabled={rejecting}>
                {rejecting ? "Rejecting…" : "Reject"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
